import { Button, BUTTON_VARIANTS, Layout, Typography } from '@community_dev/pixels'
import type { JSX } from 'react'
import { useTheme } from 'styled-components'

import type { PermissionCategory, PermissionNotice as Notice } from 'api/oauth'

export type PermissionNoticeProps = {
  notice: Notice
  redirectUri: string
  onContinue: () => unknown
  onCancel: () => unknown
}

function redirectHost(redirectUri: string): string | null {
  try {
    return new URL(redirectUri).host || null
  } catch {
    return null
  }
}

function toolCountLabel(count: number): string {
  return count === 1 ? '1 tool' : `${count} tools`
}

function accessLabel(access: PermissionCategory['access']): string {
  return access === 'read' ? 'Read only' : 'Read and write'
}

export function PermissionNotice({ notice, redirectUri, onContinue, onCancel }: PermissionNoticeProps): JSX.Element {
  const { COLORS } = useTheme() || {}
  const host = redirectHost(redirectUri)
  return (
    <>
      <Typography variant="h1">{notice.headline}</Typography>
      <Typography variant="body1">{notice.summary}</Typography>
      {notice.categories.map((category) => (
        <Layout
          aria-label={category.title}
          background={COLORS?.APP_BACKGROUND_LEVEL_3}
          borderLeft={`3px solid ${COLORS?.LINKS}`}
          borderRadius="4px"
          key={category.title}
          marginBottom="12px"
          padding="12px 16px"
          role="group"
        >
          <Layout alignItems="baseline" display="flex" justifyContent="space-between">
            <Typography component="h2" marginBottom={0} marginTop={0} variant="h5">
              {category.title}
            </Typography>
            <Typography color={COLORS?.SUBTEXT} component="span" marginBottom={0} marginTop={0} variant="caption1">
              {accessLabel(category.access)} · {toolCountLabel(category.toolCount)}
            </Typography>
          </Layout>
          <Typography marginBottom={0} marginTop="4px" variant="body2">
            {category.description}
          </Typography>
        </Layout>
      ))}
      {notice.cautions.length > 0 && (
        <Layout aria-label="Before you continue" marginTop="24px" role="group">
          <Typography component="h2" marginBottom="8px" marginTop={0} variant="h5">
            Before you continue
          </Typography>
          <Layout as="ul" margin={0} paddingLeft="20px">
            {notice.cautions.map((caution) => (
              <Typography component="li" key={caution} marginBottom="6px" marginTop={0} variant="body2">
                {caution}
              </Typography>
            ))}
          </Layout>
        </Layout>
      )}
      <Layout
        border={`1px solid ${COLORS?.BORDERS}`}
        borderRadius="8px"
        marginBottom="24px"
        marginTop="12px"
        padding="12px 16px"
      >
        <Typography marginBottom={0} marginTop={0} variant="body2">
          {host === null ? (
            'Community cannot confirm where you return after you continue. Only continue if you started this request.'
          ) : (
            <>
              After you continue, you’ll return to <strong>{host}</strong>. Only continue if you trust this app.
            </>
          )}
        </Typography>
      </Layout>
      <Layout display="flex" gap="12px">
        <Button onClick={onCancel} style={{ flex: 1 }} variant={BUTTON_VARIANTS.GHOST}>
          Cancel
        </Button>
        <Button onClick={onContinue} style={{ flex: 1 }} variant={BUTTON_VARIANTS.ACTION}>
          Allow access
        </Button>
      </Layout>
    </>
  )
}
